import { useMemo, useState, type DragEvent } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Copy, Eye, EyeOff, GripVertical, Layers, ListChecks, Plus, Search, Text, Trash2 } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { getWidget, localize, resolveWidgetKey, schemaAtPath, widgetPalette, type FormLocale, type UiField, type UiSection, type UiStep } from "@ncp/forms";
import { Badge, Button, Input, Tooltip, cn } from "@ncp/ui";
import type { FormBody } from "./api";
import { addContent, addField, addSection, addStep, isRequired, moveField, moveSection, moveStep, orphans, removeField, removeSection, removeStep, unplaced, type Selection } from "./model";
import { FieldProperties } from "./FieldProperties";

export interface DesignCanvasProps {
  body: FormBody;
  onChange: (next: FormBody | ((prev: FormBody) => FormBody), opts?: { transient?: boolean }) => void;
  selection: Selection;
  onSelect: (s: Selection) => void;
  locale: FormLocale;
  readOnly?: boolean;
  activeStep: string | null;
  onActiveStepChange: (key: string) => void;
}

const DRAG_MIME = "application/x-ncp-form-item";
type DragPayload = { kind: "palette"; widget: string } | { kind: "content" } | { kind: "field"; step: string; section: string; index: number };

function readPayload(e: DragEvent): DragPayload | null {
  try { const raw = e.dataTransfer.getData(DRAG_MIME); return raw ? (JSON.parse(raw) as DragPayload) : null; } catch { return null; }
}

/** Three-pane designer: widget palette, wizard-step canvas with sections and placements, properties of the selection. */
export function DesignCanvas({ body, onChange, selection, onSelect, locale, readOnly, activeStep, onActiveStepChange }: DesignCanvasProps) {
  const { t } = useTranslation();
  const steps = body.uiSchema.steps;
  const step = steps.find((s) => s.key === activeStep) ?? steps[0];
  const unplacedPaths = useMemo(() => unplaced(body), [body]);
  const orphanPaths = useMemo(() => orphans(body), [body]);
  const [dropTarget, setDropTarget] = useState<{ section: string; index: number } | null>(null);

  const dropInto = (e: DragEvent, sectionKey: string, index: number) => {
    e.preventDefault();
    setDropTarget(null);
    if (readOnly || !step) return;
    const payload = readPayload(e);
    if (!payload) return;
    if (payload.kind === "palette") {
      const r = addField(body, step.key, sectionKey, payload.widget, locale, index);
      onChange(r.body);
      onSelect({ kind: "field", path: r.path });
    } else if (payload.kind === "content") {
      onChange(addContent(body, step.key, sectionKey, { en: "Guidance text…", ar: "نص إرشادي…" }, index));
    } else {
      onChange(moveField(body, { step: payload.step, section: payload.section, index: payload.index }, { step: step.key, section: sectionKey, index }));
    }
  };

  const onDragOver = (e: DragEvent, sectionKey: string, index: number) => {
    if (readOnly) return;
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = readPayload(e)?.kind === "field" ? "move" : "copy";
    if (dropTarget?.section !== sectionKey || dropTarget.index !== index) setDropTarget({ section: sectionKey, index });
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)_320px]">
      {/* Palette */}
      <aside className="space-y-3 xl:sticky xl:top-20 xl:self-start">
        <Palette readOnly={readOnly} locale={locale} onAdd={(widget) => {
          if (!step) return;
          const sectionKey = step.sections[0]?.key;
          if (!sectionKey) { toastNoSection(t); return; }
          const r = addField(body, step.key, sectionKey, widget, locale);
          onChange(r.body);
          onSelect({ kind: "field", path: r.path });
        }} onAddContent={() => {
          if (!step?.sections[0]) return;
          onChange(addContent(body, step.key, step.sections[0].key, { en: "Guidance text…", ar: "نص إرشادي…" }));
        }} />
        {unplacedPaths.length > 0 ? (
          <div className="rounded-xl border border-warning-100 bg-warning-50/60 p-3 text-xs">
            <p className="mb-1.5 flex items-center gap-1 font-medium text-warning-800"><AlertTriangle className="size-3.5" /> {t("admin:forms.design.unplaced", { count: unplacedPaths.length })}</p>
            <ul className="space-y-1">
              {unplacedPaths.map((p) => (
                <li key={p} className="flex items-center justify-between gap-2">
                  <code className="truncate font-mono">{p}</code>
                  {!readOnly && step?.sections[0] ? <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={() => onChange((prev) => { const s = prev.uiSchema.steps.find((x) => x.key === step.key)!.sections[0]!; return { ...prev, uiSchema: { ...prev.uiSchema, steps: prev.uiSchema.steps.map((x) => (x.key === step.key ? { ...x, sections: x.sections.map((sec) => (sec.key === s.key ? { ...sec, fields: [...sec.fields, { path: p }] } : sec)) } : x)) } }; })}>{t("admin:forms.design.place")}</Button> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {orphanPaths.length > 0 ? (
          <div className="rounded-xl border border-danger-100 bg-danger-50/60 p-3 text-xs">
            <p className="mb-1 flex items-center gap-1 font-medium text-danger-700"><AlertTriangle className="size-3.5" /> {t("admin:forms.design.orphans", { count: orphanPaths.length })}</p>
            <p className="text-danger-700/80">{orphanPaths.map((p) => <code key={p} className="me-1 font-mono">{p}</code>)}</p>
          </div>
        ) : null}
      </aside>

      {/* Canvas */}
      <section className="min-w-0">
        <StepTabs steps={steps} active={step?.key ?? null} onSelect={(k) => { onActiveStepChange(k); onSelect({ kind: "step", step: k }); }} selection={selection} locale={locale} readOnly={readOnly}
          onAdd={(review) => { const r = addStep(body, review ? { en: "Review & submit", ar: "المراجعة والإرسال" } : { en: `Step ${steps.length + 1}`, ar: `الخطوة ${steps.length + 1}` }, review); onChange(r.body); onActiveStepChange(r.key); onSelect({ kind: "step", step: r.key }); }} />
        {!step ? (
          <div className="rounded-2xl border border-dashed border-sand-300 p-10 text-center text-sm text-muted-foreground">{t("admin:forms.design.noSteps")}</div>
        ) : (
          <div className={cn("space-y-3 rounded-2xl border p-3 transition-colors", selection?.kind === "step" && selection.step === step.key ? "border-aegold-300 bg-aegold-50/20" : "border-sand-200 bg-white/60")} onClick={() => onSelect({ kind: "step", step: step.key })}>
            <div className="flex items-start justify-between gap-3 px-1">
              <div>
                <h3 className="text-sm font-semibold">{localize(step.title, locale) || step.key}</h3>
                {step.description ? <p className="text-xs text-muted-foreground">{localize(step.description, locale)}</p> : null}
                {step.review ? <Badge variant="info" size="sm" className="mt-1"><ListChecks className="size-3" /> {t("admin:forms.design.reviewStep")}</Badge> : null}
                {step.visibleWhen !== undefined && step.visibleWhen !== true ? <Badge variant="outline" size="sm" className="ms-1 mt-1"><EyeOff className="size-3" /> {t("admin:forms.design.conditional")}</Badge> : null}
              </div>
              {!readOnly ? (
                <div className="flex items-center gap-1">
                  <Tooltip content={t("admin:forms.design.moveUp")}><Button size="icon-sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onChange(moveStep(body, step.key, -1)); }}><ChevronUp /></Button></Tooltip>
                  <Tooltip content={t("admin:forms.design.moveDown")}><Button size="icon-sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onChange(moveStep(body, step.key, 1)); }}><ChevronDown /></Button></Tooltip>
                  <Tooltip content={t("admin:forms.design.addSection")}><Button size="icon-sm" variant="ghost" onClick={(e) => { e.stopPropagation(); const r = addSection(body, step.key, { en: `Section ${step.sections.length + 1}`, ar: `القسم ${step.sections.length + 1}` }); onChange(r.body); onSelect({ kind: "section", step: step.key, section: r.key }); }}><Plus /></Button></Tooltip>
                  <Tooltip content={t("admin:forms.design.removeStep")}><Button size="icon-sm" variant="ghost" className="text-danger-600" onClick={(e) => { e.stopPropagation(); if (window.confirm(t("admin:forms.design.confirmRemoveStep"))) { onChange(removeStep(body, step.key, false)); onActiveStepChange(steps.find((s) => s.key !== step.key)?.key ?? ""); onSelect(null); } }}><Trash2 /></Button></Tooltip>
                </div>
              ) : null}
            </div>

            {step.review && step.sections.length === 0 ? (
              <div className="rounded-xl border border-dashed border-sand-300 bg-sand-50/60 p-6 text-center text-xs text-muted-foreground">{t("admin:forms.design.reviewStepHint")}</div>
            ) : null}

            {step.sections.map((section) => (
              <SectionCard key={section.key} step={step} section={section} body={body} locale={locale} readOnly={readOnly} selection={selection} onSelect={onSelect} onChange={onChange}
                dropIndex={dropTarget?.section === section.key ? dropTarget.index : null} onDragOver={onDragOver} onDrop={dropInto} onDragLeave={() => setDropTarget(null)} />
            ))}
            {step.sections.length === 0 && !step.review && !readOnly ? (
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); const r = addSection(body, step.key); onChange(r.body); }}><Plus /> {t("admin:forms.design.addSection")}</Button>
            ) : null}
          </div>
        )}
      </section>

      {/* Properties */}
      <aside className="min-w-0 xl:sticky xl:top-20 xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto xl:self-start">
        <FieldProperties body={body} onChange={onChange} selection={selection} onSelect={onSelect} locale={locale} readOnly={readOnly} />
      </aside>
    </div>
  );
}

function toastNoSection(t: (k: string) => string) {
  // eslint-disable-next-line no-alert
  window.alert(t("admin:forms.design.noSectionForAdd"));
}

// ---------------------------------------------------------------- palette

function Palette({ onAdd, onAddContent, readOnly, locale }: { onAdd: (widget: string) => void; onAddContent: () => void; readOnly?: boolean; locale: FormLocale }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const groups = useMemo(() => widgetPalette(), []);
  const term = q.trim().toLowerCase();
  return (
    <div className="rounded-xl border border-sand-200 bg-white/70 p-2">
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute start-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin:forms.design.searchWidgets")} className="h-8 ps-7 text-xs" />
      </div>
      <div className="max-h-[60vh] space-y-2 overflow-y-auto pe-1">
        {groups.map((g) => {
          const list = g.widgets.filter((w) => !term || localize(w.label, locale).toLowerCase().includes(term) || w.key.includes(term));
          if (list.length === 0) return null;
          return (
            <div key={g.category}>
              <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t(`admin:forms.widgetCategories.${g.category}`)}</p>
              <ul className="grid grid-cols-2 gap-1">
                {list.map((w) => {
                  const Icon = w.icon ?? Text;
                  return (
                    <li key={w.key}>
                      <button type="button" disabled={readOnly} draggable={!readOnly} onDragStart={(e) => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ kind: "palette", widget: w.key } satisfies DragPayload)); e.dataTransfer.effectAllowed = "copy"; }}
                        onClick={() => onAdd(w.key)} title={localize(w.description, locale)}
                        className="flex w-full flex-col items-start gap-1 rounded-lg border border-transparent bg-sand-50 px-2 py-1.5 text-start text-[11px] leading-tight hover:border-aegold-200 hover:bg-aegold-50 disabled:opacity-50">
                        <Icon className="size-3.5 text-aegold-700" />
                        <span className="line-clamp-2">{localize(w.label, locale)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
        <div>
          <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("admin:forms.widgetCategories.layout")}</p>
          <button type="button" disabled={readOnly} draggable={!readOnly} onDragStart={(e) => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ kind: "content" } satisfies DragPayload)); }} onClick={onAddContent}
            className="flex w-full flex-col items-start gap-1 rounded-lg border border-transparent bg-sand-50 px-2 py-1.5 text-start text-[11px] leading-tight hover:border-aegold-200 hover:bg-aegold-50 disabled:opacity-50">
            <Text className="size-3.5 text-aegold-700" /><span>{t("admin:forms.design.contentBlock")}</span>
          </button>
        </div>
      </div>
      <p className="mt-2 px-1 text-[10px] text-muted-foreground">{t("admin:forms.design.paletteHint")}</p>
    </div>
  );
}

// ---------------------------------------------------------------- steps

function StepTabs({ steps, active, onSelect, onAdd, selection, locale, readOnly }: { steps: UiStep[]; active: string | null; onSelect: (k: string) => void; onAdd: (review: boolean) => void; selection: Selection; locale: FormLocale; readOnly?: boolean }) {
  const { t } = useTranslation();
  void selection;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5">
      {steps.map((s, i) => (
        <button key={s.key} type="button" onClick={() => onSelect(s.key)} className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors", s.key === active ? "border-aegold-400 bg-aegold-50 font-medium text-aegold-900" : "border-sand-200 bg-white/70 hover:bg-sand-50")}>
          <span className="grid size-4 place-items-center rounded-full bg-sand-200 text-[10px] tabular">{i + 1}</span>
          {localize(s.title, locale) || s.key}
          {s.review ? <ListChecks className="size-3 text-seablue-700" /> : null}
          {s.visibleWhen !== undefined && s.visibleWhen !== true ? <EyeOff className="size-3 text-muted-foreground" /> : null}
        </button>
      ))}
      {!readOnly ? (
        <>
          <Button size="sm" variant="ghost" onClick={() => onAdd(false)}><Plus /> {t("admin:forms.design.addStep")}</Button>
          {!steps.some((s) => s.review) ? <Button size="sm" variant="ghost" onClick={() => onAdd(true)}><ListChecks /> {t("admin:forms.design.addReviewStep")}</Button> : null}
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- sections & fields

function SectionCard({ step, section, body, locale, readOnly, selection, onSelect, onChange, dropIndex, onDragOver, onDrop, onDragLeave }: {
  step: UiStep; section: UiSection; body: FormBody; locale: FormLocale; readOnly?: boolean; selection: Selection; onSelect: (s: Selection) => void; onChange: DesignCanvasProps["onChange"];
  dropIndex: number | null; onDragOver: (e: DragEvent, section: string, index: number) => void; onDrop: (e: DragEvent, section: string, index: number) => void; onDragLeave: () => void;
}) {
  const { t } = useTranslation();
  const selected = selection?.kind === "section" && selection.section === section.key && selection.step === step.key;
  const cols = section.columns ?? 2;
  return (
    <div className={cn("rounded-xl border p-3 transition-colors", selected ? "border-aegold-300 bg-aegold-50/30" : "border-sand-200 bg-white")} onClick={(e) => { e.stopPropagation(); onSelect({ kind: "section", step: step.key, section: section.key }); }}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <Layers className="size-3.5 text-muted-foreground" />
          <span className="font-medium">{localize(section.title, locale) || section.key}</span>
          <span className="text-muted-foreground">· {t("admin:forms.design.columns", { count: cols })}</span>
          {section.visibleWhen !== undefined && section.visibleWhen !== true ? <EyeOff className="size-3 text-muted-foreground" /> : null}
        </div>
        {!readOnly ? (
          <div className="flex items-center gap-0.5">
            <Button size="icon-sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onChange(moveSection(body, step.key, section.key, -1)); }}><ChevronUp /></Button>
            <Button size="icon-sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onChange(moveSection(body, step.key, section.key, 1)); }}><ChevronDown /></Button>
            <Button size="icon-sm" variant="ghost" className="text-danger-600" onClick={(e) => { e.stopPropagation(); if (section.fields.length === 0 || window.confirm(t("admin:forms.design.confirmRemoveSection"))) { onChange(removeSection(body, step.key, section.key, false)); onSelect({ kind: "step", step: step.key }); } }}><Trash2 /></Button>
          </div>
        ) : null}
      </div>
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols * 6}, minmax(0, 1fr))` }} onDragLeave={onDragLeave}>
        <DropSlot index={0} active={dropIndex === 0} onDragOver={(e) => onDragOver(e, section.key, 0)} onDrop={(e) => onDrop(e, section.key, 0)} full />
        {section.fields.map((f, i) => {
          const span = f.content ? 12 : Math.min(12, Math.max(1, f.span ?? Math.floor(12 / cols)));
          const colSpan = Math.round((span / 12) * cols * 6);
          const isSel = selection?.kind === "field" && selection.path === f.path && !!f.path;
          return (
            <div key={`${f.path || "content"}-${i}`} style={{ gridColumn: `span ${colSpan} / span ${colSpan}` }} className="contents">
              <div style={{ gridColumn: `span ${colSpan} / span ${colSpan}` }}>
                <FieldChip field={f} body={body} locale={locale} selected={isSel} readOnly={readOnly}
                  onSelect={() => f.path ? onSelect({ kind: "field", path: f.path }) : onSelect({ kind: "section", step: step.key, section: section.key })}
                  onDragStart={(e) => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ kind: "field", step: step.key, section: section.key, index: i } satisfies DragPayload)); e.dataTransfer.effectAllowed = "move"; }}
                  onRemove={() => { const del = !!f.path && window.confirm(t("admin:forms.design.confirmRemoveField")); onChange(removeField(body, step.key, section.key, i, del)); onSelect(null); }}
                  onDuplicate={f.path ? () => { const props = body.schema.properties ?? {}; const base = f.path; let k = base; let n = 2; while (props[k]) k = `${base}${n++}`; onChange((prev) => { const src = schemaAtPath(prev.schema, base); if (!src) return prev; const next = { ...prev, schema: { ...prev.schema, properties: { ...prev.schema.properties, [k]: JSON.parse(JSON.stringify(src)) } } }; next.uiSchema = { ...next.uiSchema, steps: next.uiSchema.steps.map((s) => (s.key !== step.key ? s : { ...s, sections: s.sections.map((sec) => (sec.key !== section.key ? sec : { ...sec, fields: [...sec.fields.slice(0, i + 1), { ...f, path: k }, ...sec.fields.slice(i + 1)] })) })) }; return next; }); onSelect({ kind: "field", path: k }); } : undefined} />
              </div>
              <DropSlot index={i + 1} active={dropIndex === i + 1} onDragOver={(e) => onDragOver(e, section.key, i + 1)} onDrop={(e) => onDrop(e, section.key, i + 1)} full={i === section.fields.length - 1} />
            </div>
          );
        })}
        {section.fields.length === 0 ? (
          <div className="col-span-full rounded-lg border border-dashed border-sand-300 bg-sand-50/50 p-4 text-center text-xs text-muted-foreground" onDragOver={(e) => onDragOver(e, section.key, 0)} onDrop={(e) => onDrop(e, section.key, 0)}>{t("admin:forms.design.emptySection")}</div>
        ) : null}
      </div>
    </div>
  );
}

function DropSlot({ active, onDragOver, onDrop, full }: { index: number; active: boolean; onDragOver: (e: DragEvent) => void; onDrop: (e: DragEvent) => void; full?: boolean }) {
  return <div onDragOver={onDragOver} onDrop={onDrop} className={cn("h-1.5 rounded transition-colors", full ? "col-span-full" : "col-span-full", active ? "bg-aegold-400" : "bg-transparent")} />;
}

function FieldChip({ field, body, locale, selected, readOnly, onSelect, onDragStart, onRemove, onDuplicate }: { field: UiField; body: FormBody; locale: FormLocale; selected: boolean; readOnly?: boolean; onSelect: () => void; onDragStart: (e: DragEvent) => void; onRemove: () => void; onDuplicate?: () => void }) {
  const { t } = useTranslation();
  const schema = field.path ? schemaAtPath(body.schema, field.path) : undefined;
  const missing = !!field.path && !schema;
  const widgetKey = schema ? resolveWidgetKey(schema, field) : field.content ? "content" : "?";
  const widget = getWidget(widgetKey);
  const Icon = widget?.icon ?? Text;
  const required = !!field.path && isRequired(body, field.path);
  const label = field.content ? localize(field.content, locale).slice(0, 60) : schema ? localize(schema["x-ncp"]?.title, locale) || schema.title || field.path : field.path;
  return (
    <div role="button" tabIndex={0} draggable={!readOnly} onDragStart={onDragStart} onClick={(e) => { e.stopPropagation(); onSelect(); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      className={cn("group flex h-full min-h-[52px] items-start gap-2 rounded-lg border px-2 py-1.5 text-start text-xs transition-colors", selected ? "border-aegold-400 bg-aegold-50 shadow-sm" : missing ? "border-danger-200 bg-danger-50/50" : "border-sand-200 bg-sand-50/60 hover:border-aegold-200 hover:bg-white")}>
      <GripVertical className="mt-0.5 size-3.5 shrink-0 cursor-grab text-sand-400 group-hover:text-sand-600" />
      <Icon className="mt-0.5 size-3.5 shrink-0 text-aegold-700" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{label}{required ? <span className="text-danger-600"> *</span> : null}</p>
        <p className="truncate font-mono text-[10px] text-muted-foreground" dir="ltr">{field.content ? t("admin:forms.design.contentBlock") : field.path}{widget && !field.content ? ` · ${localize(widget.label, locale)}` : ""}</p>
        <div className="mt-0.5 flex flex-wrap gap-1">
          {field.visibleWhen !== undefined && field.visibleWhen !== true ? <Badge variant="outline" size="sm"><Eye className="size-3" /> {t("admin:forms.design.conditional")}</Badge> : null}
          {field.requiredWhen !== undefined ? <Badge variant="outline" size="sm">{t("admin:forms.design.conditionallyRequired")}</Badge> : null}
          {schema?.["x-ncp"]?.sensitive ? <Badge variant="camel" size="sm">{t("admin:forms.props.sensitive")}</Badge> : null}
          {schema?.["x-ncp"]?.binding ? <Badge variant="info" size="sm">{schema["x-ncp"].binding}</Badge> : null}
          {missing ? <Badge variant="danger" size="sm">{t("admin:forms.design.missingSchema")}</Badge> : null}
        </div>
      </div>
      {!readOnly ? (
        <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {onDuplicate ? <Button size="icon-sm" variant="ghost" className="size-6" onClick={(e) => { e.stopPropagation(); onDuplicate(); }} aria-label={t("admin:forms.design.duplicate")}><Copy className="size-3" /></Button> : null}
          <Button size="icon-sm" variant="ghost" className="size-6 text-danger-600" onClick={(e) => { e.stopPropagation(); onRemove(); }} aria-label={t("admin:common.remove")}><Trash2 className="size-3" /></Button>
        </div>
      ) : null}
    </div>
  );
}
